import React, { useState, useContext, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";
import { authPost } from "../../Utilities/Api";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Register.css";

const Register = () => {
  // ----------------------------
  // Context & Navigation
  // ----------------------------
  const { login, user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  // ----------------------------
  // Form State
  // ----------------------------
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  // 🔍 DEBUG: Log Register Render
  useEffect(() => {
    console.log("📝 [Register] Component Rendered. User:", user ? user.username : "null", "Loading:", authLoading);
  }, [user, authLoading]);

  // ----------------------------
  // Theme Init
  // ----------------------------
  useEffect(() => {
    const isDark = (localStorage.getItem("theme") || "light") === "dark";
    document.documentElement.classList.toggle("dark", isDark);
  }, []);

  // ----------------------------
  // Redirect if already logged in
  // ----------------------------
  useEffect(() => {
    if (!authLoading && user?._id) {
      console.log("🔀 [Register] User is already logged in. Redirecting to /profile");
      navigate("/profile", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // ----------------------------
  // Validation Helpers
  // ----------------------------
  const validatePassword = (pw) => {
    const rules = {
      length: pw.length >= 8,
      lower: /[a-z]/.test(pw),
      upper: /[A-Z]/.test(pw),
      number: /[0-9]/.test(pw),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pw),
    };
    return { rules, isValid: Object.values(rules).every(Boolean) };
  };

  const validateUsername = (name) => {
    const clean = name.trim();
    const usernameRegex = /^[a-zA-Z0-9_.-]{3,32}$/;
    return { clean, isValid: usernameRegex.test(clean) };
  };

  // ----------------------------
  // Submit Handler
  // ----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    console.log("📨 [Register] Submit Initiated");

    if (!day || !month || !year) {
      console.warn("⚠️ [Register] Validation Failed: Date incomplete");
      toast.error("Please select a full date of birth.", { icon: "💬 talkear" });
      return;
    }

    const { clean, isValid: usernameOk } = validateUsername(username);
    if (!usernameOk) {
      console.warn("⚠️ [Register] Validation Failed: Username invalid:", clean);
      toast.error(
        "Username must be 3–32 characters and may only contain letters, numbers, dots, underscores and hyphens.",
        { icon: "💬 talkear" }
      );
      return;
    }

    const { isValid } = validatePassword(password);
    if (!isValid) {
      console.warn("⚠️ [Register] Validation Failed: Weak Password");
      toast.error("Password does not meet the requirements.", {
        icon: "💬 talkear",
      });
      return;
    }

    setSubmitting(true);

    const cleanEmail = email.trim().toLowerCase();
    const dateOfBirth = `${year}-${String(month).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;

    console.log("📦 [Register] Payload prepared. Email:", cleanEmail, "User:", clean);

    try {
      // ----------------------------
      // Register
      // ----------------------------
      console.log("📡 [Register] POST /auth/register...");
      const regRes = await authPost("/auth/register", {
        username: clean,
        email: cleanEmail,
        password,
        birthdate: dateOfBirth,
      });

      const regData = await regRes.json().catch(() => ({}));
      console.log("📥 [Register] Registration Response:", regRes.status, regData);

      if (!regRes.ok) {
        toast.error(regData?.message || "Registration failed.", {
          icon: "💬 talkear",
        });
        return;
      }

      console.log("✅ [Register] Account Created! Starting Auto-Login sequence...");
      toast.success("🎉 Account created! Logging you in...", {
        icon: "💬 talkear",
      });

      // ----------------------------
      // Auto Login
      // ----------------------------
      console.log("📡 [Register] POST /auth/login (Auto-Login)...");
      const loginRes = await authPost("/auth/login", {
        email: cleanEmail,
        password,
      });

      const loginData = await loginRes.json().catch(() => ({}));
      console.log("📥 [Register] Auto-Login Response:", loginRes.status);

      if (loginRes.ok && loginData?.accessToken && loginData?.user) {
        console.log("✅ [Register] Auto-Login Success! Updating Context...");
        login({
          accessToken: loginData.accessToken,
          accessTokenExpires: loginData.accessTokenExpires,
          user: loginData.user,
        });
        // 🚫 No navigate here — redirect handled by effect
      } else {
        console.warn("⚠️ [Register] Auto-Login Failed. User must login manually.");
        toast.error("Registered but failed to log in.", {
          icon: "💬 talkear",
        });
      }
    } catch (err) {
      console.error("❌ [Register] Critical Error:", err);
      toast.error("Something went wrong. Please try again.", {
        icon: "💬 talkear",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ----------------------------
  // Render Helpers
  // ----------------------------
  const renderDays = () =>
    [...Array(31)].map((_, i) => (
      <option key={i + 1} value={i + 1}>
        {i + 1}
      </option>
    ));

  const renderMonths = () =>
    ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map(
      (m, i) => (
        <option key={i + 1} value={i + 1}>
          {m}
        </option>
      )
    );

  const renderYears = () => {
    const currentYear = new Date().getFullYear();
    return [...Array(100)].map((_, i) => {
      const y = currentYear - i;
      return (
        <option key={y} value={y}>
          {y}
        </option>
      );
    });
  };

  const { rules } = validatePassword(password);

  // ----------------------------
  // JSX
  // ----------------------------
  return (
    <div className="page-wrapper">
      <div className="form-card">
        <h2 className="form-title">Create your talkear account</h2>

        <form onSubmit={handleSubmit} className="form-body">
          <label>Username</label>
          <input
            type="text"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={submitting}
          />

          <label>Date of birth</label>
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            <select value={day} onChange={(e) => setDay(e.target.value)} required disabled={submitting}>
              <option value="">Day</option>
              {renderDays()}
            </select>
            <select value={month} onChange={(e) => setMonth(e.target.value)} required disabled={submitting}>
              <option value="">Month</option>
              {renderMonths()}
            </select>
            <select value={year} onChange={(e) => setYear(e.target.value)} required disabled={submitting}>
              <option value="">Year</option>
              {renderYears()}
            </select>
          </div>

          <label>Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />

          <label>Password</label>
          <div className="password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (!passwordTouched) setPasswordTouched(true);
              }}
              disabled={submitting}
            />
            <span className="eye-icon" onClick={() => setShowPassword((s) => !s)}>
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          {passwordTouched && (
            <div className="password-rules">
              <ul>
                <li className={rules.length ? "valid" : "invalid"}>At least 8 characters</li>
                <li className={rules.lower ? "valid" : "invalid"}>Lowercase letter</li>
                <li className={rules.upper ? "valid" : "invalid"}>Uppercase letter</li>
                <li className={rules.number ? "valid" : "invalid"}>Number</li>
                <li className={rules.special ? "valid" : "invalid"}>Special character</li>
              </ul>
            </div>
          )}

          <button type="submit" className="form-btn" disabled={submitting}>
            {submitting ? "Registering..." : "Register"}
          </button>
        </form>

        <p className="form-switch">
          Already have an account?
          <Link
            to="/"
            onClick={() => console.log("🔗 [Register] Clicked 'Login' link")}
          >
            Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;